// import { GET_MY_AUTH } from "./ScanningVisitorsPage";
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  LinearProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  useTheme,
} from "@mui/material";
import { CartesianGrid, Tooltip, Legend, XAxis } from "recharts";
import { useQuery } from "@apollo/client";
import { formatDate, format, timestampToDate } from "../utils/dateUtils";
import { GET_MY_AUTH, GET_INSIGHTS } from "../graphql/queries";
import { COUPON_CONFIG } from "../constants/couponConstants";
import {
  SimplePieChart,
  SimpleBarChart,
  SimpleLineChart,
} from "../Components/SimpleCharts";

const InsightPage = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [chartData, setChartData] = useState({
    leadsByType: [],
    leadsByDate: [],
    scannedByStaff: [],
    allScans: [],
    staffData: [],
  });

  // Get the auth data first
  const {
    data: authData,
    loading: authLoading,
    error: authError,
  } = useQuery(GET_MY_AUTH);

  // Then fetch insights using getStandsInsights query
  const {
    data: insightsData,
    loading: insightsLoading,
    error: insightsError,
    refetch,
  } = useQuery(GET_INSIGHTS, {
    variables: {
      eventId: authData?.eventId || "",
      standId: authData?.standId || "",
    },
    skip: !authData?.eventId || !authData?.standId,
    fetchPolicy: "network-only",
  });

  // Process data when it arrives
  useEffect(() => {
    console.log("Insights Data:", insightsData);

    if (insightsData?.getInsights) {
      try {
        console.log("Raw getInsights:", insightsData.getInsights);

        const parsedData =
          typeof insightsData.getInsights === "string"
            ? JSON.parse(insightsData.getInsights)
            : insightsData.getInsights;

        console.log("Parsed Insights Data:", parsedData);

        if (parsedData) {
          // Process lead types
          const typeCount = {};
          const leadsByDate = {};
          const staffCount = {};

          if (Array.isArray(parsedData.allScans)) {
            parsedData.allScans.forEach((scan) => {
              // Count by type
              typeCount[scan.type] = (typeCount[scan.type] || 0) + 1;

              // Count by date - use timestampToDate for reliable date conversion
              const scanDate = timestampToDate(scan.created);
              if (!scanDate) {
                console.error("Invalid timestamp for scan:", scan.created);
                return;
              }

              const date = format(scanDate, "MMM d");

              leadsByDate[date] = (leadsByDate[date] || 0) + 1;

              // Count by staff - replace empty names with "admin"
              const staffName = scan.name || scan.scan_name || "";
              staffCount[
                staffName === "" || staffName === "Unknown"
                  ? "admin"
                  : staffName
              ] =
                (staffCount[
                  staffName === "" || staffName === "Unknown"
                    ? "admin"
                    : staffName
                ] || 0) + 1;
            });

            // Convert to array format for charts
            const leadsByTypeArray = Object.entries(typeCount).map(
              ([type, count]) => ({
                name: type,
                value: count,
              })
            );

            const leadsByDateArray = Object.entries(leadsByDate).map(
              ([date, count]) => ({
                date,
                count,
              })
            );

            const scannedByStaffArray = Object.entries(staffCount).map(
              ([name, count]) => ({
                name: name === "" || name === "Unknown" ? "admin" : name,
                count,
              })
            );

            setChartData({
              leadsByType: leadsByTypeArray,
              leadsByDate: leadsByDateArray.sort((a, b) => {
                // Helper function to parse "MMM d" format
                const parseMonthDay = (dateStr) => {
                  try {
                    if (!dateStr || typeof dateStr !== "string") {
                      console.error("Invalid date string:", dateStr);
                      return new Date(0); // Return epoch date for invalid input
                    }

                    const parts = dateStr.split(" ");
                    if (parts.length < 2) {
                      console.error("Invalid date format:", dateStr);
                      return new Date(0);
                    }

                    const month = [
                      "Jan",
                      "Feb",
                      "Mar",
                      "Apr",
                      "May",
                      "Jun",
                      "Jul",
                      "Aug",
                      "Sep",
                      "Oct",
                      "Nov",
                      "Dec",
                    ].indexOf(parts[0]);
                    if (month === -1) {
                      console.error("Invalid month:", parts[0]);
                      return new Date(0);
                    }

                    const day = parseInt(parts[1]);
                    if (isNaN(day)) {
                      console.error("Invalid day:", parts[1]);
                      return new Date(0);
                    }

                    return new Date(new Date().getFullYear(), month, day);
                  } catch (error) {
                    console.error("Error parsing date:", dateStr, error);
                    return new Date(0);
                  }
                };

                return parseMonthDay(a.date) - parseMonthDay(b.date);
              }),
              scannedByStaff: scannedByStaffArray,
              allScans: parsedData.allScans,
              staffData: parsedData.staffData || [],
            });

            // Debug the processed date data
            console.log("Processed leadsByDate:", leadsByDateArray);
          }
        }
      } catch (error) {
        console.error("Error parsing insights data:", error);
      }
    }
  }, [insightsData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const loading = authLoading || insightsLoading;
  const error = authError || insightsError;
  const isCouponEvent = authData?.eventId === COUPON_CONFIG.EVENT_ID;

  // Color palette for charts
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A32CC4"];

  // For coupon event: 3 tabs (Overview, Shop Performance, Detailed Coupon claims data)
  const tabLabels = isCouponEvent
    ? ["Overview Dashboard", "Shop Performance", "Detailed Coupon claims data"]
    : ["Overview Dashboard", "Leads by Type", "Leads by Date", "Staff Performance", "Detailed Data"];
  const effectiveTab = isCouponEvent
    ? (tabValue === 0 ? 0 : tabValue === 1 ? 3 : 4)
    : tabValue;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Insights Dashboard
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Insights Dashboard
        </Typography>
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Error loading insights: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: "bold",
          color: theme.palette.primary.main,
          mb: 3,
        }}
      >
        {isCouponEvent ? "Coupons Claimed Insights" : "Lead Generation Insights"}
      </Typography>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        {tabLabels.map((label, idx) => (
          <Tab key={idx} label={label} />
        ))}
      </Tabs>

      {/* Overview Dashboard */}
      {effectiveTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {isCouponEvent ? "Total coupon claimed" : "Total Leads"}
                </Typography>
                <Typography variant="h3" color="primary">
                  {chartData.allScans.length}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ height: 180 }}>
                  <SimplePieChart
                    data={chartData.leadsByType}
                    height={180}
                    width={300}
                    colors={COLORS}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  {chartData.leadsByType.map((type, index) => (
                    <Chip
                      key={type.name}
                      label={`${type.name}: ${type.value}`}
                      sx={{
                        m: 0.5,
                        backgroundColor: COLORS[index % COLORS.length],
                        color: "white",
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {isCouponEvent ? "coupons claimed over Time" : "Leads Captured Over Time"}
                </Typography>
                <Box sx={{ height: 300 }}>
                  <SimpleLineChart
                    data={chartData.leadsByDate}
                    nameKey="date"
                    dataKey="count"
                    height={300}
                    width={600}
                    stroke={theme.palette.primary.main}
                    XAxis={
                      <XAxis
                        dataKey="date"
                        tickFormatter={(value) => {
                          try {
                            return format(value, "MMM d");
                          } catch (error) {
                            console.error("Date format error:", error, value);
                            return value;
                          }
                        }}
                      />
                    }
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {isCouponEvent ? "Shop Performance" : "Staff Performance"}
                </Typography>
                <Box sx={{ height: 300 }}>
                  <SimpleBarChart
                    data={chartData.scannedByStaff}
                    nameKey="name"
                    dataKey="count"
                    height={300}
                    width={600}
                    fill={theme.palette.secondary.main}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Leads by Type */}
      {effectiveTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Lead Types Distribution
                </Typography>
                <Box sx={{ height: 300 }}>
                  <SimplePieChart
                    data={chartData.leadsByType}
                    height={300}
                    width={400}
                    colors={COLORS}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Lead Types Count
                </Typography>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Lead Type</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Percentage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chartData.leadsByType.map((row, index) => {
                        const total = chartData.allScans.length;
                        const percentage =
                          total > 0
                            ? ((row.value / total) * 100).toFixed(1)
                            : 0;

                        return (
                          <TableRow key={row.name}>
                            <TableCell component="th" scope="row">
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    bgcolor: COLORS[index % COLORS.length],
                                    mr: 1,
                                  }}
                                />
                                {row.name}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{row.value}</TableCell>
                            <TableCell align="right">{percentage}%</TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell>
                          <strong>Total</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{chartData.allScans.length}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>100%</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Leads by Date */}
      {effectiveTab === 2 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Leads Captured By Date
            </Typography>
            <Box sx={{ height: 400 }}>
              <SimpleBarChart
                data={chartData.leadsByDate}
                nameKey="date"
                dataKey="count"
                height={400}
                width={800}
                fill={theme.palette.primary.main}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Staff Performance / Shop Performance */}
      {effectiveTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {isCouponEvent
                    ? "Shop Coupon Capture Performance"
                    : "Staff Leads Capture Performance"}
                </Typography>
                <Box sx={{ height: 400 }}>
                  <SimpleBarChart
                    data={chartData.scannedByStaff}
                    nameKey="name"
                    dataKey="count"
                    height={400}
                    width={800}
                    fill={theme.palette.secondary.main}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {isCouponEvent ? "Shop Details" : "Staff Details"}
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell align="right">
                          {isCouponEvent ? "Coupons Claimed" : "Leads Captured"}
                        </TableCell>
                        <TableCell align="right">Created Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chartData.staffData.map((staff) => {
                        const leadCount =
                          chartData.scannedByStaff.find(
                            (s) =>
                              s.name === staff.name ||
                              s.name === staff.scan_name
                          )?.count || 0;

                        return (
                          <TableRow key={staff.id}>
                            <TableCell>
                              {(staff.name || staff.scan_name || "Unknown") ===
                              "Unknown"
                                ? "admin"
                                : staff.name || staff.scan_name || "admin"}
                            </TableCell>
                            <TableCell>{staff.email}</TableCell>
                            <TableCell>{staff.company}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={leadCount}
                                color={leadCount > 0 ? "success" : "default"}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              {formatDate(staff.created)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Detailed Data */}
      {effectiveTab === 4 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isCouponEvent ? "Detailed Coupon claims data" : "Detailed Lead Data"}
            </Typography>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Staff Name</TableCell>
                    <TableCell>Lead Type</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.allScans.map((scan) => (
                    <TableRow key={scan.serial}>
                      <TableCell>{scan.id}</TableCell>
                      <TableCell>
                        <Chip
                          label={scan.role}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {(scan.scan_name || scan.name || "Unknown") ===
                        "Unknown"
                          ? "admin"
                          : scan.scan_name || scan.name || "admin"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={scan.type || "Unknown"}
                          size="small"
                          color={
                            scan.type === "Hot Lead"
                              ? "error"
                              : scan.type === "Warm Lead"
                              ? "warning"
                              : scan.type === "Cold Lead"
                              ? "info"
                              : "default"
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{scan.remark || "-"}</TableCell>
                      <TableCell>{formatDate(scan.created)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default InsightPage;
